import React, { useState, useCallback, useRef } from 'react';
import styles from './TemplatePreviewPanel.module.css';

const HEADER_FIELDS = ['Title', 'Grade Level', 'Duration', 'Subject'];

const TYPE_LABELS = {
  text: 'Text',
  list: 'Bullet List',
  ordered_list: 'Numbered List',
};

const STANDARDS_ITEM = {
  name: '__standards__',
  displayName: 'Standards Alignment',
  type: 'standards',
  isStandards: true,
};

function SectionPreview({ type }) {
  if (type === 'standards') {
    return (
      <div className={styles.sectionPreview}>
        State standards linked to this lesson
      </div>
    );
  }
  if (type === 'list') {
    return (
      <div className={styles.sectionPreview}>
        <div>&bull; First item...</div>
        <div>&bull; Second item...</div>
      </div>
    );
  }
  if (type === 'ordered_list') {
    return (
      <div className={styles.sectionPreview}>
        <div>1. First item...</div>
        <div>2. Second item...</div>
      </div>
    );
  }
  return <div className={styles.sectionPreview}>Paragraph content...</div>;
}

export default function TemplatePreviewPanel({
  templateName,
  sections,
  onUpdateName,
  onUpdateSections,
  standardsPosition: controlledStdPos,
  onUpdateStandardsPosition,
}) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragCounter = useRef(0);

  // Build combined list: sections + standards interleaved at the right position
  // Standards defaults to bottom if no position provided
  const stdPos = controlledStdPos ?? sections.length;
  const combinedItems = [];
  let sectionIdx = 0;
  for (let i = 0; i <= sections.length; i++) {
    if (i === stdPos) {
      combinedItems.push({ ...STANDARDS_ITEM, combinedIndex: combinedItems.length });
    }
    if (sectionIdx < sections.length && i === sectionIdx) {
      combinedItems.push({ ...sections[sectionIdx], combinedIndex: combinedItems.length, sectionIndex: sectionIdx });
      sectionIdx++;
    }
  }
  // If standards position is past the end, append
  if (stdPos > sections.length) {
    combinedItems.push({ ...STANDARDS_ITEM, combinedIndex: combinedItems.length });
  }

  const handleStartRename = useCallback((sectionIndex) => {
    setEditingIndex(sectionIndex);
    setEditingName(sections[sectionIndex].displayName);
  }, [sections]);

  const handleFinishRename = useCallback(() => {
    if (editingIndex === null) return;
    const trimmed = editingName.trim();
    if (trimmed && trimmed !== sections[editingIndex].displayName) {
      const updated = sections.map((s, i) =>
        i === editingIndex ? { ...s, displayName: trimmed } : s
      );
      onUpdateSections(updated);
    }
    setEditingIndex(null);
    setEditingName('');
  }, [editingIndex, editingName, sections, onUpdateSections]);

  const handleChangeType = useCallback(
    (sectionIndex, newType) => {
      const updated = sections.map((s, i) =>
        i === sectionIndex ? { ...s, type: newType } : s
      );
      onUpdateSections(updated);
    },
    [sections, onUpdateSections]
  );

  const handleRemove = useCallback(
    (sectionIndex) => {
      onUpdateSections(sections.filter((_, i) => i !== sectionIndex));
      // Adjust standards position if needed
      if (onUpdateStandardsPosition && sectionIndex < stdPos) {
        onUpdateStandardsPosition(stdPos - 1);
      }
    },
    [sections, onUpdateSections, onUpdateStandardsPosition, stdPos]
  );

  const handleAddSection = useCallback(() => {
    let count = sections.length + 1;
    const existingNames = new Set(sections.map(s => s.name));
    while (existingNames.has(`section_${count}`)) {
      count++;
    }
    const newSection = {
      name: `section_${count}`,
      displayName: `New Section ${count}`,
      type: 'text',
    };
    onUpdateSections([...sections, newSection]);
  }, [sections, onUpdateSections]);

  // Drag and drop handlers
  const handleDragStart = useCallback((e, combinedIdx) => {
    setDragIndex(combinedIdx);
    e.dataTransfer.effectAllowed = 'move';
    // Needed for Firefox
    e.dataTransfer.setData('text/plain', String(combinedIdx));
  }, []);

  const handleDragEnter = useCallback((e, combinedIdx) => {
    e.preventDefault();
    dragCounter.current++;
    setDragOverIndex(combinedIdx);
  }, []);

  const handleDragLeave = useCallback(() => {
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      setDragOverIndex(null);
      dragCounter.current = 0;
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e, targetCombinedIdx) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOverIndex(null);

    if (dragIndex === null || dragIndex === targetCombinedIdx) {
      setDragIndex(null);
      return;
    }

    const dragItem = combinedItems[dragIndex];
    const targetItem = combinedItems[targetCombinedIdx];

    if (!dragItem || !targetItem) {
      setDragIndex(null);
      return;
    }

    const isMovingStandards = dragItem.isStandards;
    const isTargetStandards = targetItem.isStandards;

    if (isMovingStandards) {
      // Moving standards: calculate new position among sections
      const newStdPos = targetItem.isStandards
        ? stdPos
        : targetCombinedIdx < dragIndex
          ? (targetItem.sectionIndex ?? 0)
          : (targetItem.sectionIndex ?? 0) + 1;
      if (onUpdateStandardsPosition) {
        onUpdateStandardsPosition(newStdPos);
      }
    } else if (isTargetStandards) {
      // Moving a section onto standards: put it where standards is
      const fromIdx = dragItem.sectionIndex;
      const updated = [...sections];
      const [moved] = updated.splice(fromIdx, 1);
      // Insert at standards position
      const insertAt = stdPos > fromIdx ? stdPos - 1 : stdPos;
      updated.splice(insertAt, 0, moved);
      onUpdateSections(updated);
    } else {
      // Moving a section to another section position
      const fromIdx = dragItem.sectionIndex;
      const toIdx = targetItem.sectionIndex;
      if (fromIdx === undefined || toIdx === undefined || fromIdx === toIdx) {
        setDragIndex(null);
        return;
      }
      const updated = [...sections];
      const [moved] = updated.splice(fromIdx, 1);
      updated.splice(toIdx, 0, moved);
      onUpdateSections(updated);

      // Adjust standards position if items moved across it
      if (onUpdateStandardsPosition) {
        let newStd = stdPos;
        if (fromIdx < stdPos && toIdx >= stdPos) newStd = stdPos - 1;
        else if (fromIdx >= stdPos && toIdx < stdPos) newStd = stdPos + 1;
        if (newStd !== stdPos) onUpdateStandardsPosition(newStd);
      }
    }

    setDragIndex(null);
  }, [dragIndex, combinedItems, sections, stdPos, onUpdateSections, onUpdateStandardsPosition]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragOverIndex(null);
    dragCounter.current = 0;
  }, []);

  return (
    <div className={styles.panel}>
      <input
        className={styles.nameInput}
        type="text"
        value={templateName}
        onChange={(e) => onUpdateName(e.target.value)}
        placeholder="Template name"
        aria-label="Template name"
      />

      {/* Locked header section */}
      <div className={styles.lockedSection}>
        <div className={styles.lockedLabel}>
          <span className={styles.lockIcon} aria-hidden="true">
            &#128274;
          </span>
          Header Fields
        </div>
        <div className={styles.lockedFields}>
          {HEADER_FIELDS.map((field) => (
            <span key={field} className={styles.lockedChip}>
              {field}
            </span>
          ))}
        </div>
      </div>

      {/* Draggable sections + standards */}
      {combinedItems.length === 0 ? (
        <div className={styles.emptySections}>
          No sections yet. Add a section to get started.
        </div>
      ) : (
        combinedItems.map((item, idx) => {
          const isDragging = dragIndex === idx;
          const isDragOver = dragOverIndex === idx && dragIndex !== idx;

          if (item.isStandards) {
            return (
              <div
                key="__standards__"
                className={`${styles.standardsItem} ${isDragging ? styles.dragging : ''} ${isDragOver ? styles.dragOver : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragEnter={(e) => handleDragEnter(e, idx)}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, idx)}
                onDragEnd={handleDragEnd}
              >
                <div className={styles.sectionHeader}>
                  <div className={styles.dragHandle} aria-label="Drag to reorder" title="Drag to reorder">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                      <circle cx="4" cy="2" r="1.2" />
                      <circle cx="8" cy="2" r="1.2" />
                      <circle cx="4" cy="6" r="1.2" />
                      <circle cx="8" cy="6" r="1.2" />
                      <circle cx="4" cy="10" r="1.2" />
                      <circle cx="8" cy="10" r="1.2" />
                    </svg>
                  </div>
                  <div className={styles.sectionInfo}>
                    <span className={styles.standardsDisplayName}>
                      Standards Alignment
                    </span>
                    <span className={styles.standardsNote}>Required section</span>
                  </div>
                </div>
                <SectionPreview type="standards" />
              </div>
            );
          }

          return (
            <div
              key={item.name + item.sectionIndex}
              className={`${styles.sectionItem} ${isDragging ? styles.dragging : ''} ${isDragOver ? styles.dragOver : ''}`}
              draggable
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragEnter={(e) => handleDragEnter(e, idx)}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, idx)}
              onDragEnd={handleDragEnd}
            >
              <div className={styles.sectionHeader}>
                <div className={styles.dragHandle} aria-label="Drag to reorder" title="Drag to reorder">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                    <circle cx="4" cy="2" r="1.2" />
                    <circle cx="8" cy="2" r="1.2" />
                    <circle cx="4" cy="6" r="1.2" />
                    <circle cx="8" cy="6" r="1.2" />
                    <circle cx="4" cy="10" r="1.2" />
                    <circle cx="8" cy="10" r="1.2" />
                  </svg>
                </div>

                <div className={styles.sectionInfo}>
                  {editingIndex === item.sectionIndex ? (
                    <input
                      className={styles.renameInput}
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={handleFinishRename}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleFinishRename();
                        if (e.key === 'Escape') {
                          setEditingIndex(null);
                          setEditingName('');
                        }
                      }}
                      autoFocus
                      aria-label="Section name"
                    />
                  ) : (
                    <span
                      className={styles.sectionDisplayName}
                      onClick={() => handleStartRename(item.sectionIndex)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleStartRename(item.sectionIndex);
                      }}
                      title="Click to rename"
                    >
                      {item.displayName}
                    </span>
                  )}
                  <code className={styles.sectionSlug}>
                    {'{{ ' + item.name + ' }}'}
                  </code>
                </div>

                <select
                  className={styles.typeSelect}
                  value={item.type}
                  onChange={(e) => handleChangeType(item.sectionIndex, e.target.value)}
                  aria-label="Section type"
                >
                  {Object.entries(TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>

                <button
                  className={styles.removeBtn}
                  onClick={() => handleRemove(item.sectionIndex)}
                  aria-label="Remove section"
                  title="Remove"
                >
                  &#10005;
                </button>
              </div>

              <SectionPreview type={item.type} />
            </div>
          );
        })
      )}

      <button className={styles.addSectionBtn} onClick={handleAddSection}>
        + Add Section
      </button>
    </div>
  );
}
